/**
 * Progressive stream accumulator — never exposes unsafe cut points inside
 * tables, fenced blocks, math, or citation tokens.
 */

export function findSafeStreamCutIndex(text, targetIndex) {
    const max = Math.min(Math.max(0, targetIndex), String(text || '').length);
    let index = max;

    index = clampOpenFence(text, index);
    index = clampIncompleteTableRow(text, index);
    index = clampIncompleteMath(text, index);
    index = clampIncompleteCitation(text, index);

    return index;
}

function clampOpenFence(text, index) {
    const before = text.slice(0, index);
    const fences = before.match(/```/g);
    if (!fences || fences.length % 2 === 0) return index;
    const lastOpen = before.lastIndexOf('```');
    return lastOpen >= 0 ? lastOpen : 0;
}

function clampIncompleteTableRow(text, index) {
    if (index <= 0) return index;
    const lineStart = text.lastIndexOf('\n', index - 1) + 1;
    const lineEnd = text.indexOf('\n', lineStart);
    const line = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd);
    if (!line.includes('|')) return index;
    const trimmed = line.trimStart();
    if (!trimmed.startsWith('|')) return index;
    if (!line.trimEnd().endsWith('|')) {
        return lineStart;
    }
    return index;
}

function clampIncompleteMath(text, index) {
    const before = text.slice(0, index);
    const blockDelims = before.match(/\$\$/g);
    if (blockDelims && blockDelims.length % 2 === 1) {
        const last = before.lastIndexOf('$$');
        return last >= 0 ? last : index;
    }
    return index;
}

function clampIncompleteCitation(text, index) {
    const before = text.slice(0, index);
    const lastOpen = before.lastIndexOf('[');
    if (lastOpen === -1) return index;
    const tail = before.slice(lastOpen);
    if (/^\[\d*$/.test(tail)) {
        return lastOpen;
    }
    return index;
}

export class StreamChunkAccumulator {
    constructor() {
        this.buffer = '';
        this.targetLength = 0;
    }

    reset() {
        this.buffer = '';
        this.targetLength = 0;
    }

    setFull(text) {
        this.buffer = String(text || '');
        this.targetLength = 0;
    }

    append(chunk) {
        this.buffer += String(chunk || '');
    }

    revealTo(length) {
        this.targetLength = Math.max(this.targetLength, length);
    }

    getDisplayContent() {
        const safeIndex = findSafeStreamCutIndex(this.buffer, this.targetLength);
        return this.buffer.slice(0, safeIndex);
    }

    getFullContent() {
        return this.buffer;
    }

    isFullyRevealed() {
        return (
            this.buffer.length > 0 &&
            this.getDisplayContent().length === this.buffer.length &&
            this.targetLength >= this.buffer.length
        );
    }
}

export function appendStreamChunk(accumulator, chunk) {
    const acc = accumulator instanceof StreamChunkAccumulator ? accumulator : new StreamChunkAccumulator();
    acc.append(chunk);
    return acc.getDisplayContent();
}
